//-- Copyright (c) 2021 TMT Insights Group, LLC
import { AllTableColumnsConfig } from '@/__configs/config_types';
import {
  AssigneeSelect,
  ClickCopy,
  DateTimeDisplay,
  History,
  PendingAssetRowExpand,
  StatusDropdown,
  Notes,
  LinkAsset
} from '@tmt-insights/oms_fe_core/columns';
import { insertWordBreaks } from '@tmt-insights/oms_fe_core/utils';
import { Box, Typography } from '@tmt-insights/oms_fe_core/material';
import { OrderJob, OrderJobAsset, UserGroup, UserProfile, TitleEntity } from '@/redux/slices/slice_types';

const columns: AllTableColumnsConfig<OrderJob & OrderJobAsset> = {
  expandable: {
    label: '',
    isExpandable: true,
    render: (props: { item: OrderJob; isCollapsed: boolean }) => <PendingAssetRowExpand {...props} />
  },
  hid: {
    label: 'Task ID',
    sortField: 'id',
    render: (cell: OrderJobAsset['hid']) => <strong>{cell}</strong>
  },
  jobs__due_date: {
    label: 'Due Date',
    minWidth: 100,
    render: (_cell: any, row: OrderJobAsset) => <DateTimeDisplay>{row.jobs__due_date}</DateTimeDisplay>
  },
  jobs: {
    label: 'Title Name',
    sortField: 'jobs__title__meta__series_title',
    minWidth: 200,
    render: (cell: OrderJobAsset['jobs']) => {
      if (!cell) return '-';
      const title = cell?.[0]?.title;
      if (!title) return '-';
      const { entity_type = 'feature', name = '', sequence_order, lineage_meta = {} } = title;
      return (
        <div style={{ lineHeight: 1.5 }}>
          <div>
            <strong>{insertWordBreaks(lineage_meta.series_name || name)}</strong>
          </div>
          <Typography variant="subtitle2" sx={{ fontSize: '.95rem', lineHeight: 1.4, mt: 0.5, opacity: 0.5, fontWeight: 500 }}>
            {entity_type === 'episode' && (
              <>
                S{lineage_meta.season_sequence_order} &bull; E{sequence_order} &bull;
              </>
            )}
            {lineage_meta.series_name && <>&quot;{name}&quot;</>}
          </Typography>
        </div>
      );
    }
  },
  filename: {
    label: 'Asset Name',
    minWidth: 280,
    render: (cell: OrderJobAsset['filename'], row: OrderJobAsset) => (
      <>
        <ClickCopy copyText={cell}>
          <strong>{insertWordBreaks(cell)}</strong>
        </ClickCopy>

        <Typography variant="subtitle2" sx={{ fontSize: '.95rem', lineHeight: 1.4, opacity: 0.5, fontWeight: 500 }}>
          {row.base_path || ''}
        </Typography>
      </>
    )
  },
  jobs__version__name: {
    label: 'Version',
    render: (cell: TitleEntity['name']) => insertWordBreaks(cell)
  },
  assigned_group: {
    label: 'Assigned Group',
    render: (cell: UserGroup, row: OrderJobAsset) => {
      return <AssigneeSelect value={cell} id={row.id} assigns="group" isAsset />;
    },
    minWidth: 200,
    disableSort: true
  },
  assigned_user: {
    label: 'Assignee',
    render: (cell: OrderJobAsset['assigned_user'], row: OrderJobAsset) => (
      <AssigneeSelect value={cell as UserProfile} id={row.id} assigns="user" isAsset />
    ),
    minWidth: 200,
    disableSort: true
  },
  status: {
    label: 'Status',
    render: (cell: OrderJobAsset['status'], row: OrderJobAsset) => <StatusDropdown id={row.id} status={cell} />
  },
  history: {
    label: 'History',
    disableSort: true,
    render: (_cell: any, row: OrderJobAsset) => (
      <Box sx={{ ml: 1 }}>
        <History data={row} iconOnly />
      </Box>
    )
  },
  notes: {
    label: 'Notes',
    render: (_cell: any, row: OrderJobAsset) => (
      <div style={{ width: 40, textAlign: 'center' }}>
        <Notes data={row} type="jobs" />
      </div>
    ),
    disableSort: true
  },
  link_action: {
    label: 'Link',
    disableSort: true,
    render: (_cell: any, row: OrderJobAsset) => <LinkAsset disabled={row.status !== 'awaiting_ingest'} data={row} />
  }
};

export default columns;
